import { Box, Drawer, Grid, Typography } from '@mui/material';
import CustomTextField from '../../../../../components/customized/customtextfield/CustomTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import CustomButton from '../../../../../components/buttons/CustomButton';
import CustomSelect from '../../../../../components/customized/customselect/CustomSelect';
import CustomDateCalendar from '../../../../../components/customized/customcalendar/CustomCalendar';
import dayjs from 'dayjs';
import { convertToEpoch2 } from '../../../../../../utils/commonFunctions';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import Resizer from 'react-image-file-resizer';

const logisticsSchema: any = yup.object().shape({
  truckNumber: yup
    .string()
    .matches(/^[A-Za-z0-9\s]+$/, 'Truck Number must contains only alphabets or numbers')
    .typeError('Truck Number must be a string')
    .required('Enter the Truck Number'),
  unitNumber: yup
    .string()
    .matches(/^[A-Za-z0-9\s]+$/, 'Unit Number must contains only alphabets or numbers')
    .typeError('Unit Number must be a string')
    .required('Enter the Unit Number'),
  numberOfAxles: yup
    .number()
    .min(1, 'Axle Number must be at least 1')
    .typeError('Axle Number must be a number')
    .required('Enter the Axle Number'),
  yearOfPurchase: yup
    .number()
    .min(1, 'Year of Purchased must be at least 1')
    .typeError('Year of Purchased must be a number')
    .required('Enter the Year of Purchased'),
  truckUnladenWeight: yup
    .number()
    .min(1, 'Truck Unladen Weight must be at least 1')
    .typeError('Truck Unladen Weight must be a number')
    .required('Enter the Truck Unladen Weight'),
  weightUnits: yup
    .string()
    .typeError('Weight Unit must be a string')
    .required('Select Weight Unit'),
  height: yup
    .number()
    .min(1, 'Height must be at least 1')
    .typeError('Height must be a number')
    .required('Enter the Height'),
  dimensionalUnits: yup
    .string()
    .typeError('Dimensional Unit must be a string')
    .required('Select Dimensional Unit'),
  grossVehicleWeight: yup
    .number()
    .min(1, 'Gross Vehicle Weight must be at least 1')
    .typeError('Gross Vehicle Weight must be a number')
    .required('Enter the Gross Vehicle Weight'),
  serialNumber: yup
    .string()
    .matches(/^[A-Za-z0-9\s]+$/, 'Serial Number must contains only alphabets or numbers')
    .typeError('Serial Number must be a string')
    .required('Enter the Serial Number'),
  dotInspectionDate: yup
    .string()
    .typeError('DOT Inspection Date must be a string')
    .required('Select DOT Inspection Date'),
  insuranceDocument: yup.string().notRequired(),
  insuranceExpiryDate: yup
    .string()
    .typeError('Insurance Expiry Date must be a string')
    .required('Select Insurance Expiry Date'),
  insuranceNumber: yup.string().required('Enter the Insurance Number'),
  licenseDocument: yup.string().notRequired(),
  licenseExpiryDate: yup
    .string()
    .typeError('License Expiry Date be a string')
    .required('Select License Expiry Date'),
  licenseNumber: yup.string().required('Enter the License Number'),
  fitnessDocument: yup.string().notRequired(),
  fitnessExpiryDate: yup
    .string()
    .typeError('Fitness Expiry Date be a string')
    .required('Select Fitness Expiry Date'),
  fitnessNumber: yup.string().required('Enter the Fitness Number')
});

interface UpdateLogistics {
  open: boolean;
  setOpen: any;
  roleType: string;
  filteredRow: any;
}

interface TextField {
  id: string;
  label: string;
  field:
    | 'truckNumber'
    | 'unitNumber'
    | 'numberOfAxles'
    | 'yearOfPurchase'
    | 'truckUnladenWeight'
    | 'weightUnits'
    | 'height'
    | 'dimensionalUnits'
    | 'grossVehicleWeight'
    | 'serialNumber'
    | 'dotInspectionDate'
    | 'insuranceDocument'
    | 'insuranceExpiryDate'
    | 'insuranceNumber'
    | 'licenseDocument'
    | 'licenseExpiryDate'
    | 'licenseNumber'
    | 'fitnessDocument'
    | 'fitnessExpiryDate'
    | 'fitnessNumber';
  placeholder: string;
  inputProps: {
    maxLength?: number;
    style?: React.CSSProperties;
  };
  options?: { id: string; label: string }[];
}

const UpdateLogistics = ({ open, setOpen, roleType, filteredRow }: UpdateLogistics) => {
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const [insuranceFiles, setInsuranceFiles] = useState<File[]>([]);
  const [fitnessFiles, setFitnessFiles] = useState<File[]>([]);

  const { control, handleSubmit, reset, setValue, clearErrors } = useForm({
    resolver: yupResolver(logisticsSchema)
  });

  const textField: TextField[] = [
    {
      id: '1',
      label: 'Truck Number',
      field: 'truckNumber',
      placeholder: 'Truck Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '2',
      label: 'Unit Number',
      field: 'unitNumber',
      placeholder: 'Unit Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '3',
      label: 'Number of Axle',
      field: 'numberOfAxles',
      placeholder: 'Number of Axle',
      inputProps: { maxLength: 10 }
    },
    {
      id: '4',
      label: 'Year of Purchase',
      field: 'yearOfPurchase',
      placeholder: 'Year of Purchase',
      inputProps: { maxLength: 10 }
    },
    {
      id: '5',
      label: 'Unladen Weight Units',
      field: 'weightUnits',
      placeholder: 'Weight Units',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'kg', label: 'kg' },
        { id: 'ton', label: 'ton' },
        { id: 'pounds', label: 'pounds' }
      ]
    },
    {
      id: '6',
      label: ' Unladen Weight',
      field: 'truckUnladenWeight',
      placeholder: 'Unladen Weight',
      inputProps: { maxLength: 10 }
    },
    {
      id: '7',
      label: 'Dimensional Units',
      field: 'dimensionalUnits',
      placeholder: 'Dimensional Unit',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'm', label: 'm' },
        { id: 'ft', label: 'ft' }
      ]
    },
    {
      id: '8',
      label: 'Height',
      field: 'height',
      placeholder: 'Height',
      inputProps: { maxLength: 10 }
    },
    {
      id: '9',
      label: 'Gross Vehicle Weight',
      field: 'grossVehicleWeight',
      placeholder: 'Gross Vehicle Weight',
      inputProps: { maxLength: 10 }
    },
    {
      id: '10',
      label: 'Serial Number',
      field: 'serialNumber',
      placeholder: 'Serial Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '11',
      label: 'DOT Inspection Date',
      field: 'dotInspectionDate',
      placeholder: 'DOT Inspection Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '12',
      label: 'Insurance Number',
      field: 'insuranceNumber',
      placeholder: 'Insurance Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '13',
      label: 'Insurance Expiry Date',
      field: 'insuranceExpiryDate',
      placeholder: 'Insurance Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '14',
      label: 'Insurance Document',
      field: 'insuranceDocument',
      placeholder: 'Insurance Document',
      inputProps: { maxLength: 10 }
    },
    {
      id: '15',
      label: 'License Number',
      field: 'licenseNumber',
      placeholder: 'License Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '16',
      label: 'License Expiry Date',
      field: 'licenseExpiryDate',
      placeholder: 'License Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '17',
      label: 'License Document',
      field: 'licenseDocument',
      placeholder: 'License Document',
      inputProps: { maxLength: 10 }
    },
    {
      id: '18',
      label: 'Fitness Number',
      field: 'fitnessNumber',
      placeholder: 'Fitness Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '19',
      label: 'Fitness Expiry Date',
      field: 'fitnessExpiryDate',
      placeholder: 'Fitness Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '20',
      label: 'Fitness Document',
      field: 'fitnessDocument',
      placeholder: 'Fitness Document',
      inputProps: { maxLength: 10 }
    }
  ];

  const handleUpload = async (type: 'license' | 'insurance' | 'fitness') => {
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = 'image/*';
    inputElement.multiple = true;

    inputElement.onchange = async (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files) return;

      const fileArray = Array.from(files);

      for (const file of fileArray) {
        Resizer.imageFileResizer(
          file,
          1920,
          1920,
          'JPEG',
          50,
          0,
          (uri: any) => {
            const blob = new Blob([uri], { type: 'image/jpeg' });
            const resizedFile = new File([blob], file.name, { type: 'image/jpeg' });

            if (type === 'license') {
              setLicenseFiles(prev => [...prev, resizedFile]);
            }
            if (type === 'insurance') {
              setInsuranceFiles(prev => [...prev, resizedFile]);
            }
            if (type === 'fitness') {
              setFitnessFiles(prev => [...prev, resizedFile]);
            }
          },
          'blob'
        );
      }

      clearErrors();
    };

    inputElement.click();
  };

  const handleRemoveFile = (type: 'license' | 'insurance' | 'fitness', index: number) => {
    if (type === 'license') {
      setLicenseFiles(prev => prev.filter((_, i) => i !== index));
    }
    if (type === 'insurance') {
      setInsuranceFiles(prev => prev.filter((_, i) => i !== index));
    }
    if (type === 'fitness') {
      setFitnessFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const onSubmit = async (data: any) => {};

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  return (
    <>
      <Box p={2}>
        <Drawer
          anchor='right'
          open={open}
          onClose={() => {
            setOpen(false);
            reset();
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px !important'
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>{roleType}</Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 15 }}
              className='logistics-container'
            >
              <Box component={'form'} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={1}>
                  {textField?.map(item => (
                    <Grid
                      item
                      key={item.id}
                      xs={
                        item.id === '5' ||
                        item.id === '6' ||
                        item.id === '7' ||
                        item.id === '8'
                          ? 6
                          : 12
                      }
                      mt={item.id === '5' || item.id === '7' ? 1 : 0}
                      className='logistics-item'
                    >
                      {item.options ? (
                        <CustomSelect
                          control={control}
                          name={item.field}
                          placeholder={item.placeholder}
                          id={item.id}
                          label={item.label}
                          options={item.options}
                        />
                      ) : item.id === '11' ||
                        item.id === '13' ||
                        item.id === '16' ||
                        item.id === '19' ? (
                        <CustomDateCalendar
                          id={item.id}
                          name={item.field}
                          className='calendar'
                          control={control}
                          disableFuture={false}
                          disablePast={false}
                          placeholder={item.placeholder}
                          label={item.label}
                          value={
                            filteredRow
                              ? dayjs.unix(
                                  item.field === 'licenseExpiryDate' &&
                                    filteredRow?.licenseExpireDate !== null
                                    ? filteredRow?.licenseExpireDate
                                    : item.field === 'insuranceExpiryDate' &&
                                      filteredRow?.insuranceExpireDate !== null
                                    ? filteredRow?.insuranceExpireDate
                                    : item.field === 'fitnessExpiryDate' &&
                                      filteredRow?.fitnessExpireDate !== null
                                    ? filteredRow?.fitnessExpireDate
                                    : item.field === 'dotInspectionDate' &&
                                      filteredRow?.dotInspectionDate !== null
                                    ? filteredRow?.dotInspectionDate
                                    : filteredRow?.dotInspectionDate
                                )
                              : null
                          }
                          onDateChange={(date: any) => {
                            const epoc = convertToEpoch2(date?.$d);
                            setValue(item.field, epoc !== null ? String(epoc) : '');
                            clearErrors(item.field);
                          }}
                        />
                      ) : item.field === 'licenseDocument' ||
                        item.field === 'insuranceDocument' ||
                        item.field === 'fitnessDocument' ? (
                        <Grid container alignItems='center'>
                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif !important",
                                fontSize: '13px !important',
                                fontWeight: '500 !important',
                                color: '#26134b',
                                marginTop: '25px'
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <CustomButton
                              sx={{
                                backgroundColor: 'white',
                                color: '#26134b',
                                border: '1px solid #26134b',
                                '&:hover': {
                                  backgroundColor: 'white',
                                  color: '#26134b',
                                  border: '1px solid #26134b'
                                },
                                marginTop: '25px'
                              }}
                              className='upload'
                              type='button'
                              category='Upload Files'
                              onClick={() =>
                                handleUpload(
                                  item.field === 'licenseDocument'
                                    ? 'license'
                                    : item.field === 'insuranceDocument'
                                    ? 'insurance'
                                    : 'fitness'
                                )
                              }
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        <CustomTextField
                          key={item.id}
                          control={control}
                          name={item.field}
                          placeholder={item.placeholder}
                          id={item.id}
                          type={null}
                          label={item.label}
                          inputProps={item.inputProps}
                          disabled={item.id === '1' ? true : false}
                        />
                      )}
                      {item.field === 'licenseDocument' && (
                        <Grid container>
                          {licenseFiles.map((file, index) => (
                            <Grid item key={index} xs={12}>
                              <Box
                                display='flex'
                                alignItems='center'
                                sx={{ marginTop: '10px' }}
                              >
                                <Typography>{file.name}</Typography>
                                <Icon
                                  icon='carbon:close-outline'
                                  style={{
                                    color: '#c52626',
                                    marginLeft: '2px',
                                    marginTop: '2px'
                                  }}
                                  onClick={() => handleRemoveFile('license', index)}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      {item.field === 'insuranceDocument' && (
                        <Grid container>
                          {insuranceFiles.map((file, index) => (
                            <Grid item key={index} xs={12}>
                              <Box
                                display='flex'
                                alignItems='center'
                                sx={{ marginTop: '10px' }}
                              >
                                <Typography>{file.name}</Typography>
                                <Icon
                                  icon='carbon:close-outline'
                                  style={{
                                    color: '#c52626',
                                    marginLeft: '2px',
                                    marginTop: '1px'
                                  }}
                                  onClick={() => handleRemoveFile('insurance', index)}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      {item.field === 'fitnessDocument' && (
                        <Grid container>
                          {fitnessFiles.map((file, index) => (
                            <Grid item key={index} xs={12}>
                              <Box
                                display='flex'
                                alignItems='center'
                                sx={{ marginTop: '10px' }}
                              >
                                <Typography>{file.name}</Typography>
                                <Icon
                                  icon='carbon:close-outline'
                                  style={{
                                    color: '#c52626',
                                    marginLeft: '2px',
                                    marginTop: '1px'
                                  }}
                                  onClick={() => handleRemoveFile('fitness', index)}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ marginTop: '5%', display: 'flex', gap: '15px' }}>
                  <CustomButton
                    className='saveChanges'
                    type='submit'
                    category={roleType}
                  />
                  <CustomButton
                    className='cancel'
                    category='Cancel'
                    onClick={() => {
                      handleClose();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </>
  );
};

export default UpdateLogistics;
